import { TransactionSide } from '@client/graphql/__generated__/types';

interface MetadataParticipants {
  sellers?: Array<{ name: string; email: string }>;
  listingAgents?: Array<{ name: string; email: string }>;
  buyers?: Array<{ name: string; email: string }>;
  buyerAgents?: Array<{ name: string; email: string }>;
  otherTeamMembers?: Array<{ name: string; email: string }>;
}

interface Metadata {
  agent_name?: string;
  folder_name?: string;
  transaction_side?: string;
  participants?: MetadataParticipants;
  folder_id?: string;
  office_id?: string;
  office_name?: string;
  organization_id?: string;
  transaction_closing_date?: string;
}

/**
 * Extracts agent and client names from ingestion folder metadata
 * based on the transaction side (buy/sell)
 */
export function extractNamesFromMetadata(
  metadata: Record<string, unknown> | string | undefined,
  transactionSide: string
): { agentName: string; clientName: string } {
  if (!metadata) {
    return { agentName: '-', clientName: '-' };
  }

  let parsedMetadata: Metadata;
  try {
    if (typeof metadata === 'string') {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const parsed = JSON.parse(metadata);
      parsedMetadata = parsed as Metadata;
    } else {
      parsedMetadata = metadata as Metadata;
    }
  } catch {
    return { agentName: '-', clientName: '-' };
  }

  const participants = parsedMetadata.participants || {};

  let agentName = '-';
  let clientName = '-';

  if (transactionSide === TransactionSide.BUY) {
    // For buy side: get buyer agent and buyer
    if (participants.buyerAgents && participants.buyerAgents.length > 0) {
      agentName = participants.buyerAgents[0].name;
    }
    if (participants.buyers && participants.buyers.length > 0) {
      clientName = participants.buyers[0].name;
    }
  } else if (transactionSide === TransactionSide.SELL) {
    // For sell side: get listing agent and seller
    if (participants.listingAgents && participants.listingAgents.length > 0) {
      agentName = participants.listingAgents[0].name;
    }
    if (participants.sellers && participants.sellers.length > 0) {
      clientName = participants.sellers[0].name;
    }
  }

  // Fallback to agent_name from metadata if not found in participants
  if (agentName === '-' && parsedMetadata.agent_name) {
    agentName = parsedMetadata.agent_name;
  }

  return { agentName, clientName };
}
